import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePowerMeters, getPowerMeterDetailsRoute } from 'src/Libs/power-meters'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { useDispatch } from 'react-redux'
import { useHeaderBreadcrumbs } from '@e3dc-react/shell/Libs/Header'
import { Breadcrumb } from '@e3dc-react/shell/Elements/Header/Breadcrumbs'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { PowerMeterSettingsSkeleton } from 'src/Routes/SystemSettings/Settings/SystemSettingsSkeletons'
import { BlankViewImageVariants, BlankView } from 'src/Components/blank-view'

interface Props {
  installationId?: string
  storageId?: string
  parentBreadcrumb: Breadcrumb
}

/**
 * The PowerMeterDispatchPage is a fallback in case no power meter information are loaded for a given storage hardware.
 *
 * As soon as valid power meter data is available, the component forwards to the next accessible PowerMeter
 *
 * @constructor
 */
const PowerMeterDispatchPage: React.FC<Props> = ({ storageId, installationId, parentBreadcrumb }) => {
  const { loadAllForStorage, powerMeters, error } = usePowerMeters(storageId)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, selectedAppLanguage } = useCustomTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const title = t('framework.powermeter')
  const { setBreadcrumbs } = useHeaderBreadcrumbs()
  const { enqueueErrorStack } = useSnackbar()

  useEffect(() => {
    dispatch(appUpdate({ title }))
    setBreadcrumbs([parentBreadcrumb, { title }])

    return () => setBreadcrumbs([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppLanguage, parentBreadcrumb])

  useEffect(() => {
    if (!powerMeters.length) {
      setIsLoading(true)
      loadAllForStorage()
        .catch(error => {
          console.error(error)
          enqueueErrorStack(t('settings.powermeter.load.error'))
        })
        .finally(() => setIsLoading(false))
    }
  }, [powerMeters.length, storageId, history]) // eslint-disable-line

  useEffect(() => {
    if (powerMeters.length > 0) {
      navigate(getPowerMeterDetailsRoute(installationId, storageId, powerMeters[0]?.listIndex), { replace: true })
    }
  }, [powerMeters]) // eslint-disable-line

  const isNoDataAvailable = !isLoading && powerMeters.length === 0

  if (isLoading) return <PowerMeterSettingsSkeleton />

  if (isNoDataAvailable) {
    return (
      <BlankView
        title={'systemSettings.api.no-data-available.title'}
        explanation={'systemSettings.api.no-data-available.explanation'}
        imageVariant={error ? BlankViewImageVariants.Worried : BlankViewImageVariants.Neutral}
      />
    )
  }

  return null
}

export default PowerMeterDispatchPage
